import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { applyDir } from "@/i18n";
import { useCurrency, CURRENCIES } from "@/lib/currency";

export default function NavBar() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const { currency, setCurrency, fmt } = useCurrency();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!user) { setBalance(null); return; }
    api.get("/wallet").then(({ data }) => setBalance(data.balance)).catch(() => {});
  }, [user]);

  const switchLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lm_lang", lng);
    applyDir(lng);
  };

  const links = user
    ? [
        { to: "/marketplace", label: t("nav_marketplace") },
        user.role === "landlord" && { to: "/landlord", label: t("nav_landlord") },
        user.role === "investor" && { to: "/portfolio", label: t("nav_portfolio") },
        user.role === "tenant"   && { to: "/tenant",   label: t("nav_tenant") },
        user.role === "amc"      && { to: "/amc",      label: t("nav_amc") },
        { to: "/activity", label: t("nav_activity") },
      ].filter(Boolean)
    : [];

  return (
    <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-black/10">
      <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <span className="w-6 h-6 bg-black inline-block" />
          <span className="font-display text-xl font-black tracking-tight">{t("brand")}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-mono-label text-xs px-3 py-2 transition ${
                  isActive ? "text-[#002FA7]" : "text-zinc-700 hover:text-black"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          {/* Currency switcher */}
          <select data-testid="currency-select" value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="border border-black/20 font-mono-label text-[10px] px-2 py-1 bg-white hover:bg-black/5 cursor-pointer">
            {Object.values(CURRENCIES).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </select>
          {/* Language switcher */}
          <div className="flex items-center border border-black/20 font-mono-label text-[10px] overflow-hidden">
            <button data-testid="lang-ar" onClick={() => switchLang("ar")}
              className={`px-2 py-1 ${i18n.language === "ar" ? "bg-black text-white" : "hover:bg-black/5"}`}>ع</button>
            <button data-testid="lang-en" onClick={() => switchLang("en")}
              className={`px-2 py-1 ${i18n.language === "en" ? "bg-black text-white" : "hover:bg-black/5"}`}>EN</button>
          </div>

          {user ? (
            <>
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="font-mono-label text-[10px] text-zinc-500">{t("nav_wallet")}</span>
                <span data-testid="nav-balance" className="font-display text-sm font-bold tabular-nums">
                  {balance == null ? "—" : fmt(balance)}
                </span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="nav-user-btn" variant="outline" className="rounded-none border-black h-9">
                    {user.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none w-56">
                  <DropdownMenuLabel className="font-mono-label text-[10px]">
                    {user.role}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet">{t("nav_wallet")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/activity")} data-testid="menu-activity">{t("nav_activity")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); navigate("/"); }}
                    data-testid="menu-logout"
                  >
                    {t("nav_signout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="rounded-none" data-testid="nav-login">{t("nav_signin")}</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#001F70]" data-testid="nav-register">
                  {t("nav_getstarted")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
